import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Wallet, Search, User, MoreHorizontal } from "lucide-react";

const TABS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testId: "mobile-nav-dashboard" },
  { to: "/earnings", icon: Wallet, label: "Wallet", testId: "mobile-nav-wallet", matchPrefix: "/earnings" },
  { to: "/campaigns", icon: Search, label: "Campaigns", testId: "mobile-nav-campaigns", matchPrefix: "/campaigns", center: true },
  { to: "/socials", icon: User, label: "Accounts", testId: "mobile-nav-accounts" },
];

function isPathActive(pathname, to, matchPrefix) {
  if (matchPrefix) return pathname === matchPrefix || pathname.startsWith(matchPrefix + "/");
  return pathname === to;
}

function NavTab({ tab }) {
  const { pathname } = useLocation();
  const active = isPathActive(pathname, tab.to, tab.matchPrefix);
  const Icon = tab.icon;

  if (tab.center) {
    return (
      <NavLink
        to={tab.to}
        data-testid={tab.testId}
        aria-label={tab.label}
        className="relative -mt-7 flex w-[64px] flex-col items-center"
      >
        <span
          className={[
            "flex h-14 w-14 items-center justify-center rounded-full transition-all duration-300",
            "border border-white/15 backdrop-blur-xl",
            active
              ? "bg-gradient-to-br from-[#c8ff00]/35 via-[#c8ff00]/15 to-zinc-900/40 text-[#2d3d00] shadow-[0_8px_26px_-6px_rgba(200,255,0,0.55)] dark:text-[#d6ff5c] dark:shadow-[0_8px_26px_-4px_rgba(200,255,0,0.45)]"
              : "bg-zinc-900/10 text-zinc-700 shadow-[0_6px_22px_-8px_rgba(15,23,42,0.35)] dark:bg-white/[0.06] dark:text-zinc-200 dark:shadow-[0_6px_22px_-8px_rgba(0,0,0,0.7)]",
            "active:scale-90",
          ].join(" ")}
        >
          <Icon size={22} strokeWidth={active ? 2.2 : 1.85} />
        </span>
        <span className={`mt-1 text-[10.5px] font-medium leading-none tracking-tight ${active ? "text-zinc-900 dark:text-[#d6ff5c]" : "text-zinc-500 dark:text-zinc-400"}`}>
          {tab.label}
        </span>
      </NavLink>
    );
  }

  return (
    <NavLink
      to={tab.to}
      data-testid={tab.testId}
      aria-label={tab.label}
      className="flex flex-1 flex-col items-center justify-center gap-1 py-1.5"
    >
      <span
        className={[
          "flex h-9 min-w-[44px] items-center justify-center rounded-[12px] px-2 transition-all duration-300",
          active
            ? "bg-[#c8ff00]/15 text-[#2d3d00] ring-1 ring-inset ring-[#c8ff00]/30 shadow-[0_0_14px_-6px_rgba(200,255,0,0.45)] dark:bg-[#c8ff00]/12 dark:text-[#d6ff5c] dark:ring-[#c8ff00]/20"
            : "text-zinc-500 dark:text-zinc-400",
          "active:scale-90",
        ].join(" ")}
      >
        <Icon size={19} strokeWidth={active ? 2.1 : 1.75} />
      </span>
      <span className={`text-[10.5px] font-medium leading-none tracking-tight ${active ? "text-zinc-900 dark:text-[#d6ff5c]" : "text-zinc-500 dark:text-zinc-500"}`}>
        {tab.label}
      </span>
    </NavLink>
  );
}

export function MobileBottomNav({ onOpenMore, moreActive = false }) {
  return (
    <div
      data-testid="mobile-bottom-nav"
      className="fixed bottom-0 left-0 z-50 w-full"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div
        data-testid="mobile-bottom-nav-card"
        className={[
          "relative flex items-end justify-between gap-1 px-3 pb-2 pt-2.5",
          "border-t backdrop-blur-2xl",
          "border-zinc-200/80 bg-white/95 shadow-[0_-4px_18px_-8px_rgba(15,23,42,0.14)]",
          "dark:border-white/[0.06] dark:bg-[#1C1C1D]/95 dark:shadow-[0_-4px_18px_-6px_rgba(0,0,0,0.55)]",
        ].join(" ")}
      >
        {/* Left: 2 tabs */}
        <NavTab tab={TABS[0]} />
        <NavTab tab={TABS[1]} />
        {/* Center: campaigns elevated */}
        <NavTab tab={TABS[2]} />
        {/* Right: accounts + more */}
        <NavTab tab={TABS[3]} />

        <button
          data-testid="mobile-nav-more"
          aria-label="More"
          onClick={() => onOpenMore?.()}
          className="flex flex-1 flex-col items-center justify-center gap-1 py-1.5 active:scale-95"
        >
          <span
            className={[
              "flex h-9 min-w-[44px] items-center justify-center rounded-[12px] px-2 transition-all duration-300",
              moreActive
                ? "bg-[#c8ff00]/15 text-[#2d3d00] ring-1 ring-inset ring-[#c8ff00]/30 dark:bg-[#c8ff00]/12 dark:text-[#d6ff5c] dark:ring-[#c8ff00]/20"
                : "text-zinc-500 dark:text-zinc-400",
            ].join(" ")}
          >
            <MoreHorizontal size={19} strokeWidth={moreActive ? 2.1 : 1.75} />
          </span>
          <span className={`text-[10.5px] font-medium leading-none tracking-tight ${moreActive ? "text-zinc-900 dark:text-[#d6ff5c]" : "text-zinc-500 dark:text-zinc-500"}`}>
            More
          </span>
        </button>
      </div>
    </div>
  );
}
